import { useRef, useState } from 'react'

import {
  Avatar,
  Box,
  Button,
  type Colors,
  Icon,
  Text,
  Tooltip,
  Touchable,
  useToggle,
} from '@factorialco/deprecated-design-system'

import buildUrl from 'modules/core/lib/buildUrl'
import { InjectHoverProvider } from 'modules/core/lib/hoverContext'

import { ResultValidation } from 'modules/payroll_bundle/hooks/useResultValidations'
import { usePayrollResults } from 'modules/payroll_bundle/lib/PayrollResultContext'
import { UPDATE_DATA } from 'modules/payroll_bundle/lib/constants'
import {
  ResultValidationStatus,
  ResultValidationStatusType,
  isResultValidationStatus,
} from 'modules/payroll_bundle/lib/constants'
import { formatAmount } from 'modules/payroll_bundle/lib/formatNumber'
import { usePolicyPeriods } from 'modules/payroll_bundle/lib/policyPeriodsContext'

import Formula from './_Formula'

export const colorsForValidationStatus = (
  status: ResultValidationStatusType | string
): Record<'color' | 'outline', Colors> => {
  if (!isResultValidationStatus(status)) {
    return { color: 'radicalRed600', outline: 'radicalRed200' }
  }

  switch (status) {
    case ResultValidationStatus.valid:
      return { color: 'lime1000', outline: 'lime200' }
    case ResultValidationStatus.warning:
      return { color: 'champagne1000', outline: 'champagne400' }
    case ResultValidationStatus.invalid:
      return { color: 'radicalRed600', outline: 'radicalRed200' }
    default:
      return { color: 'radicalRed600', outline: 'radicalRed200' }
  }
}

interface ValidationProps {
  item: ResultValidation
  currency: string
  last?: boolean
  withValidationName?: boolean
}

const Validation = ({ item, currency, withValidationName, last }: ValidationProps) => {
  const { resultDispatch } = usePayrollResults()

  const [showFormula, toggleShowFormula] = useState<boolean>(false)
  const { currentPeriod } = usePolicyPeriods()

  const [openEmployeeNameTooltip, toggleEmployeeNameTooltip] = useToggle(false)
  const employeeNameRef = useRef<HTMLParagraphElement>(null)
  const currentEmployeeNameRef = employeeNameRef?.current
  const isOverflowing =
    !!currentEmployeeNameRef &&
    currentEmployeeNameRef.offsetWidth < currentEmployeeNameRef.scrollWidth

  const leftSide = withValidationName ? (
    <Box flexDirection="row" gap="s12" alignItems="center">
      <Box>
        <Icon.Small
          encircled={colorsForValidationStatus(item.status)?.outline}
          icon="circle"
          color={colorsForValidationStatus(item.status)?.color}
        />
      </Box>
      <Box flexDirection="row" gap="s8" alignItems="center">
        <Text.Normal size="200" weight="medium" color="grey1000">
          {item.validation?.name}
        </Text.Normal>
        <Box>
          <Icon.Tiny icon="chevron-right" color="grey800" />
        </Box>
      </Box>
    </Box>
  ) : (
    <>
      <Avatar
        size="s24"
        label="item.employee.fullName"
        url={buildUrl(item.employee?.access?.avatar)}
      />
      <Touchable
        onClick={() =>
          resultDispatch({
            type: UPDATE_DATA,
            payload: {
              showResultValidationsSidePanel: false,
              selectedEmployeeId: item.employee?.id,
            },
          })
        }
      >
        <Box flexDirection="row" alignItems="center" flexGrow gap="s8">
          <Tooltip.Alt
            attachment="top left"
            targetAttachment="middle right"
            size="small"
            open={isOverflowing && openEmployeeNameTooltip}
            toggle={toggleEmployeeNameTooltip}
            label={item.employee?.fullName}
          >
            <InjectHoverProvider>
              <Box maxWidth="s240" flexDirection="row" justifyContent="flexStart">
                <Text.Normal
                  size="200"
                  weight="medium"
                  ellipsis
                  innerRef={employeeNameRef}
                  overflowWrap
                  color="grey1000"
                >
                  {item.employee?.fullName}
                </Text.Normal>
              </Box>
            </InjectHoverProvider>
          </Tooltip.Alt>
          <Box>
            <Icon.Tiny icon="chevron-right" color="grey800" />
          </Box>
        </Box>
      </Touchable>
    </>
  )

  return (
    <>
      <Box
        key={item.employee?.id}
        flexDirection="row"
        alignItems="center"
        justifyContent="spaceBetween"
        {...(!last && {
          border: {
            bottom: { width: 's1', style: 'solid', color: 'grey400' },
          },
        })}
      >
        <Box
          justifyContent="flexStart"
          flexDirection="row"
          gap="s12"
          padding="s12"
          alignItems="center"
          width="s300"
        >
          {leftSide}
        </Box>

        <Box
          flexDirection="row"
          alignItems="center"
          justifyContent="flexEnd"
          gap="s12"
          padding="s12"
        >
          <Text.Normal
            color={
              item.status == ResultValidationStatus.invalid ? 'radicalRed1200' : 'champagne1000'
            }
            size="100"
          >
            {item.status != ResultValidationStatus.valid
              ? formatAmount(item.difference, currency)
              : ''}
          </Text.Normal>
          <Button
            type="secondary"
            onClick={() => toggleShowFormula((prevShowFormula) => !prevShowFormula)}
            iconName="calculator"
          />
        </Box>
      </Box>
      {showFormula && (
        <>
          {last && (
            <Box border={{ bottom: { width: 's1', style: 'solid', color: 'grey400' } }}></Box>
          )}
          {item?.employee?.id && item.validation && (
            <Formula
              policyPeriodId={currentPeriod?.id}
              employeeIds={[item.employee.id]}
              validationFormula={item.validation}
              currency={currency}
            />
          )}
          {!last && (
            <Box border={{ bottom: { width: 's1', style: 'solid', color: 'grey400' } }}></Box>
          )}
        </>
      )}
    </>
  )
}

export default Validation
